import type { ErrorInfo } from 'react';

import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import { filterCommonAttributes } from './analytics-helpers';
import { getErrorAttributes } from './get-error-attributes';
import { toHttpErrorCategory } from './http-error-category';
import type { commonAttributesType } from './types';

type AIAnswerNotShownProps = commonAttributesType & {
	isStreamed: boolean;
	loadTime?: number;
	fmpLoadTime?: number;
	error?: unknown;
	errorInfo?: ErrorInfo;
};

// When there is no ai generated answer to the question
export const onAIAnswerNotShown = ({
	isStreamed,
	loadTime,
	fmpLoadTime,
	error,
	errorInfo,
	...props
}: AIAnswerNotShownProps): AnalyticsEventPayload => {
	const {
		query,
		answerString,
		source,
		errorState,
		errorCode,
		networkErrorReason,
		networkErrorDetails,
		backendFailureType,
		networkErrorDiagnosticType,
		networkErrorFailureStage,
		responseContentType,
		networkErrorTraceId,
		streamEventsReceived,
		networkErrorDuration,
		extraAttributes,
	} = props;
	return {
		action: 'shown',
		actionSubject: 'aiAnswerError',
		source: source,
		eventType: 'track',
		attributes: {
			...filterCommonAttributes(props),
			isStreamed,
			loadTime,
			fmpLoadTime,
			errorState,
			...(fg('kd_sain_person_card_error_details') && getErrorAttributes(error, errorInfo)),
			...(fg('kd_granular_network_errors') && {
				errorCode,
				...(fg('kd_http_error_status_propagation') && {
					httpErrorCategory: toHttpErrorCategory(errorCode),
				}),
				networkErrorReason,
				networkErrorDetails,
				// Prefer the trace id captured from the backend error response (e.g.
				// Stargate's atl-traceid) so failures can be correlated with
				// backend/gateway logs; fall back to the client request id.
				traceId: networkErrorTraceId ?? extraAttributes?.requestId,
				backendFailureType,
				streamEventsReceived,
				networkErrorDuration,
			}),
			...(fg('kd_sain_stream_error_diagnostics') && {
				networkErrorDiagnosticType,
				networkErrorFailureStage,
				responseContentType,
				...(networkErrorTraceId && { traceId: networkErrorTraceId }),
			}),
		},
		nonPrivacySafeAttributes: {
			query,
			answerString,
		},
	};
};
